import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import UnfoldMoreIcon from "@material-ui/icons/UnfoldMore";
import UnfoldLessIcon from "@material-ui/icons/UnfoldLess";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import MicIcon from "@material-ui/icons/Mic";
import MicOffIcon from "@material-ui/icons/MicOff";
import AddClassService from "./Locality/Service/addClassService";
import SectionService from "./Locality/Service/sectionService";
import StudentService from "./Locality/Service/studentService";
import SuperActivityService from "./Locality/Service/superActivityService";
import ActivityTabelService from "./Locality/Service/activityTabelService";
import ActivityService from "./Locality/Service/activityService";
import AcademicYearService from "./Locality/Service/academicYearService";
import ImagesStoreService from "./Locality/Service/imagesStoreService";

const KEY_SEQUENCE = ["P", "WA", "WI", "M"];
const MAX_IMAGES = 10;
const IMAGES_STORE_ALL_KEY = "ALL";

const getDefaultAcademicYearLabel = () => {
  const y = new Date().getFullYear();
  return `${y}-${y + 1}`;
};

const STATUS_META = {
  P: { label: "Presented", short: "P", bg: "#1e4d7b", color: "#fff" },
  WA: { label: "Work with Assistance", short: "WA", bg: "#7eb8e8", color: "#0f2d4d" },
  WI: { label: "Work Independently", short: "WI", bg: "#4a90c8", color: "#fff" },
  M: { label: "Mastered", short: "M", bg: "#0a1f3d", color: "#fff" },
};

const normalizeId = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    if (value.$oid) return String(value.$oid);
    if (value._id) return normalizeId(value._id);
    if (value.id) return normalizeId(value.id);
  }
  return String(value);
};

const getUserContext = () => {
  let userDetails = {};
  try {
    userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}") || {};
  } catch {
    userDetails = {};
  }
  const schooleId =
    userDetails?.schooleId?._id ||
    userDetails?.schooleId ||
    userDetails?.schoolId?._id ||
    userDetails?.schoolId ||
    "";
  return { userDetails, schooleId: schooleId ? String(schooleId) : "" };
};

const getTodayIso = () => new Date().toISOString().split("T")[0];

const recordCellKey = (studentId, activityId, subActivityId) =>
  `${normalizeId(studentId)}|${normalizeId(activityId)}|${normalizeId(subActivityId) || "null"}`;

const exerciseRowKey = (activityId, subActivityId) =>
  `${normalizeId(activityId)}|${normalizeId(subActivityId) || "null"}`;

const nextStatusKey = (current) => {
  const normalized = String(current || "").trim().toUpperCase();
  if (!normalized) return "P";
  const idx = KEY_SEQUENCE.indexOf(normalized);
  if (idx < 0) return "P";
  if (idx >= KEY_SEQUENCE.length - 1) return "";
  return KEY_SEQUENCE[idx + 1];
};

/** Step one key back: M → WI → WA → P → empty. */
const prevStatusKey = (current) => {
  const normalized = String(current || "").trim().toUpperCase();
  if (!normalized) return "";
  const idx = KEY_SEQUENCE.indexOf(normalized);
  if (idx < 0) return "";
  if (idx === 0) return "";
  return KEY_SEQUENCE[idx - 1];
};

const studentDisplayName = (s) => {
  const first = String(s?.firstName || "").trim();
  const last = String(s?.lastName || "").trim();
  const full = `${first} ${last}`.trim();
  return full || s?.name || "Student";
};

const calculateAgeFromDob = (dob) => {
  if (!dob) return null;
  const birthDate = new Date(dob);
  if (Number.isNaN(birthDate.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - birthDate.getFullYear();
  let months = now.getMonth() - birthDate.getMonth();
  if (now.getDate() < birthDate.getDate()) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years < 0) return null;
  return { years, months };
};

const formatStudentAge = (dob) => {
  const age = calculateAgeFromDob(dob);
  if (!age) return "";
  return `${age.years}y : ${age.months}m`;
};

const formatHalfYearAge = (totalMonths) => {
  const years = totalMonths / 12;
  return Number.isInteger(years) ? String(years) : years.toFixed(1);
};

const getAgeGroupLabel = (dob) => {
  const age = calculateAgeFromDob(dob);
  if (!age) return "Age N/A";
  const totalMonths = age.years * 12 + age.months;
  const bucketStart = Math.floor(totalMonths / 6) * 6;
  const bucketEnd = bucketStart + 6;
  return `${formatHalfYearAge(bucketStart)} to ${formatHalfYearAge(bucketEnd)}`;
};

const getAgeGroupSortKey = (dob) => {
  const age = calculateAgeFromDob(dob);
  if (!age) return Infinity;
  return Math.floor((age.years * 12 + age.months) / 6) * 6;
};

const buildAgeWiseSummary = (students) => {
  const groups = new Map();
  students.forEach((student) => {
    const label = student.ageGroupLabel || getAgeGroupLabel(student.dob);
    if (!groups.has(label)) {
      groups.set(label, { sortKey: getAgeGroupSortKey(student.dob), count: 0 });
    }
    groups.get(label).count += 1;
  });
  return Array.from(groups.entries())
    .sort(([, a], [, b]) => a.sortKey - b.sortKey)
    .map(([label, group]) => [label, group.count]);
};

const isRecordPublished = (record) =>
  String(record?.publicStatus || "").toLowerCase() === "publish";

const getRowNotes = (activity, subActivity) => {
  const exerciseNotes = String(subActivity?.notes || "").trim();
  if (exerciseNotes) return exerciseNotes;
  return String(activity?.notes || "").trim();
};

const hasCatalogContent = (row) => {
  if (!row) return false;
  if (String(row?.notes || "").trim()) return true;
  if (Array.isArray(row?.imageURL) && row.imageURL.filter(Boolean).length) return true;
  if (Array.isArray(row?.videoURL) && row.videoURL.filter(Boolean).length) return true;
  return false;
};

const resolveMediaGalleryPath = (activity, subActivity = null) => {
  if (!activity?._id) return "";
  if (subActivity?._id && hasCatalogContent(subActivity)) {
    return `/app/activity-media/${subActivity._id}?type=exercise`;
  }
  if (hasCatalogContent(activity)) {
    return `/app/activity-media/${activity._id}`;
  }
  const subs = Array.isArray(activity.subActivities) ? activity.subActivities : [];
  const withContent = subs.filter(hasCatalogContent);
  if (withContent.length === 1) {
    return `/app/activity-media/${withContent[0]._id}?type=exercise`;
  }
  return `/app/activity-media/${activity._id}`;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 88px)",
    minHeight: "calc(100vh - 88px)",
    maxHeight: "calc(100vh - 88px)",
    background: "#f4f7fb",
    borderRadius: 16,
    overflow: "hidden",
    border: "1px solid #e2e8f0",
    boxShadow: "0 10px 28px rgba(15, 45, 77, 0.08)",
    [theme.breakpoints.down("sm")]: {
      height: "calc(100vh - 72px)",
      minHeight: "calc(100vh - 72px)",
      maxHeight: "calc(100vh - 72px)",
      borderRadius: 12,
    },
  },
  topBar: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
    borderBottom: "1px solid #e2e8f0",
    flexWrap: "wrap",
  },
  backBtn: {
    flexShrink: 0,
    background: "#eef4ff",
    color: "#1769d6",
    "&:hover": { background: "#dbe7ff" },
  },
  classTabs: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    alignItems: "center",
    flex: "1 1 180px",
    minWidth: 0,
  },
  classTab: {
    cursor: "pointer",
    padding: "6px 12px",
    borderRadius: 999,
    border: "1px solid #d7e0ea",
    backgroundColor: "#ffffff",
    color: "#475569",
    fontWeight: 600,
    fontSize: "0.8rem",
    whiteSpace: "nowrap",
    userSelect: "none",
    lineHeight: 1.2,
    boxSizing: "border-box",
  },
  classTabActive: {
    borderColor: "#1769d6",
    backgroundColor: "#1769d6",
    color: "#ffffff",
    fontWeight: 700,
    boxShadow: "0 6px 14px rgba(23, 105, 214, 0.28)",
  },
  sectionSelect: {
    minWidth: 100,
    maxWidth: 140,
    flex: "0 0 auto",
    background: "#fff",
    borderRadius: 10,
    "& .MuiOutlinedInput-root": { borderRadius: 10 },
    "& .MuiOutlinedInput-input": {
      padding: "8px 32px 8px 12px",
      fontWeight: 600,
      fontSize: "0.85rem",
    },
  },
  areaBar: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    background: "#ffffff",
    borderBottom: "1px solid #e2e8f0",
    flexWrap: "wrap",
  },
  areaSelectWrap: {
    flex: "1 1 220px",
    minWidth: 0,
    maxWidth: 380,
  },
  areaSelect: {
    width: "100%",
    background: "linear-gradient(135deg, #0f3b62 0%, #1769d6 100%)",
    borderRadius: 10,
    color: "#fff",
    fontWeight: 700,
    fontSize: "0.95rem",
    "& .MuiSelect-select": {
      padding: "10px 36px 10px 14px",
      color: "#fff",
    },
    "& .MuiSelect-icon": { color: "#fff" },
    "&:before, &:after": { display: "none" },
  },
  toolBtn: {
    color: "#64748b",
    padding: 6,
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    background: "#fff",
  },
  nameToggle: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.75rem",
    padding: "4px 10px",
    minWidth: 0,
    lineHeight: 1.2,
    borderRadius: 8,
    borderColor: "#d7e0ea",
    color: "#334155",
  },
  bulkBar: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    padding: "10px 14px",
    background: "#eff6ff",
    borderBottom: "1px solid #bfdbfe",
  },
  bulkKeyBtn: {
    minWidth: 36,
    height: 32,
    padding: "0 10px",
    fontWeight: 800,
    fontSize: "0.78rem",
    borderRadius: 8,
    textTransform: "none",
  },
  bulkHint: {
    fontSize: "0.75rem",
    color: "#64748b",
    fontWeight: 600,
  },
  gridWrap: {
    flex: "1 1 auto",
    minHeight: "calc(100vh - 300px)",
    overflow: "auto",
    background: "#fff",
  },
  table: {
    borderCollapse: "separate",
    borderSpacing: 0,
    tableLayout: "fixed",
    width: "max-content",
  },
  stickyCorner: {
    position: "sticky",
    left: 0,
    top: 0,
    zIndex: 4,
    background: "#fff",
    width: 200,
    minWidth: 200,
    maxWidth: 200,
    borderBottom: "1px solid #e2e6eb",
    borderRight: "1px solid #e2e6eb",
    padding: "6px 8px",
    verticalAlign: "bottom",
    boxSizing: "border-box",
  },
  studentHead: {
    position: "sticky",
    top: 0,
    zIndex: 3,
    background: "#fff",
    borderBottom: "1px solid #e2e6eb",
    verticalAlign: "bottom",
    padding: "4px 2px 6px",
    width: 52,
    minWidth: 52,
    maxWidth: 52,
    boxSizing: "border-box",
  },
  studentHeadFull: {
    width: 58,
    minWidth: 58,
    maxWidth: 58,
  },
  studentHeadSelected: {
    background: "#eff6ff",
  },
  applyColBtn: {
    display: "block",
    margin: "4px auto 0",
    padding: "2px 4px",
    minWidth: 0,
    width: "100%",
    maxWidth: 48,
    fontSize: "0.6rem",
    fontWeight: 800,
    textTransform: "none",
    color: "#1769d6",
    borderColor: "#93c5fd",
    height: 20,
    lineHeight: 1,
  },
  applyRowBtn: {
    flex: "1 0 100%",
    marginLeft: 0,
    marginTop: 2,
    padding: "2px 6px",
    minWidth: 0,
    fontSize: "0.65rem",
    fontWeight: 700,
    textTransform: "none",
    color: "#1769d6",
    borderColor: "#93c5fd",
    height: 22,
  },
  studentName: {
    writingMode: "vertical-rl",
    transform: "rotate(180deg)",
    whiteSpace: "nowrap",
    fontSize: "0.72rem",
    fontWeight: 600,
    color: "#334155",
    height: 72,
    maxHeight: 72,
    overflow: "hidden",
    textOverflow: "ellipsis",
    letterSpacing: 0.15,
    margin: "0 auto",
    display: "block",
  },
  studentNameFull: {
    height: "auto",
    maxHeight: 220,
    overflow: "visible",
    textOverflow: "clip",
  },
  groupRow: {
    background: "#f4f6f8",
  },
  groupCell: {
    position: "sticky",
    left: 0,
    zIndex: 2,
    background: "#f4f6f8",
    borderBottom: "1px solid #e2e6eb",
    borderRight: "1px solid #e2e6eb",
    padding: "5px 8px",
    cursor: "pointer",
    userSelect: "none",
    width: 200,
    minWidth: 200,
    maxWidth: 200,
    boxSizing: "border-box",
  },
  groupTitle: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontWeight: 700,
    fontSize: "0.8rem",
    color: "#1e293b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 176,
  },
  groupFill: {
    background: "#f4f6f8",
    borderBottom: "1px solid #e2e6eb",
    width: 52,
    minWidth: 52,
    maxWidth: 52,
    boxSizing: "border-box",
  },
  activityCell: {
    position: "sticky",
    left: 0,
    zIndex: 1,
    background: "#fff",
    borderBottom: "1px solid #eef1f4",
    borderRight: "1px solid #e2e6eb",
    padding: "4px 6px 4px 10px",
    width: 200,
    minWidth: 200,
    maxWidth: 200,
    boxSizing: "border-box",
  },
  activityLabel: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    minWidth: 0,
    maxWidth: "100%",
    flexWrap: "wrap",
  },
  activityIcon: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    background: "#d5dbe3",
    color: "#5a6570",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "& svg": {
      fontSize: 12,
    },
  },
  activityName: {
    flex: "1 1 auto",
    minWidth: 0,
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#334155",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  activityRowSelected: {
    background: "#f0f7ff",
  },
  activityRowCheck: {
    padding: 0,
    marginRight: 2,
    flexShrink: 0,
    "& .MuiSvgIcon-root": { fontSize: 16 },
  },
  statusTd: {
    borderBottom: "1px solid #eef1f4",
    padding: 3,
    textAlign: "center",
    verticalAlign: "middle",
    width: 52,
    minWidth: 52,
    maxWidth: 52,
    boxSizing: "border-box",
  },
  statusTdSelected: {
    background: "#f8fbff",
  },
  cellActions: {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
  },
  addCellBtn: {
    padding: 0,
    width: 18,
    height: 18,
    color: "#1769d6",
    backgroundColor: "#e8f0fe",
    "&:hover": {
      backgroundColor: "#d2e3fc",
    },
    "& svg": {
      fontSize: 14,
    },
  },
  addCellBtnFilled: {
    color: "#ffffff",
    backgroundColor: "#2e7d32",
    "&:hover": {
      backgroundColor: "#1b5e20",
    },
  },
  statusBtn: {
    width: 24,
    height: 24,
    borderRadius: 3,
    border: "1.5px solid #c5ccd6",
    background: "#fff",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.68rem",
    fontWeight: 800,
    lineHeight: 1,
    padding: 0,
    transition: "box-shadow 0.12s ease, transform 0.08s ease",
    "&:hover": {
      boxShadow: "0 0 0 2px rgba(34, 197, 94, 0.45)",
    },
    "&:disabled": {
      opacity: 0.55,
      cursor: "wait",
    },
  },
  statusBtnActive: {
    boxShadow: "0 0 0 2px #22c55e",
  },
  emptyState: {
    padding: theme.spacing(6, 2),
    textAlign: "center",
    color: "#64748b",
  },
  legend: {
    flexShrink: 0,
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
    padding: "10px 14px",
    background: "#ffffff",
    borderTop: "1px solid #e2e8f0",
  },
  legendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: "0.72rem",
    color: "#475569",
    fontWeight: 600,
  },
  legendSwatch: {
    width: 16,
    height: 16,
    borderRadius: 3,
    border: "1px solid #c5ccd6",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.62rem",
    fontWeight: 800,
  },
  legendPlus: {
    width: 16,
    height: 16,
    borderRadius: 3,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1,
  },
  legendPlusBlue: {
    color: "#1769d6",
    backgroundColor: "#e8f0fe",
  },
  legendPlusGreen: {
    color: "#ffffff",
    backgroundColor: "#2e7d32",
  },
  loadingOverlay: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(8),
  },
  ageBar: {
    flexShrink: 0,
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 6,
    padding: "8px 12px",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e6eb",
  },
  ageLabel: {
    width: "100%",
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#46566b",
    marginBottom: 2,
  },
  drawerPaper: {
    width: 380,
    maxWidth: "100vw",
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  topLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#94a3b8",
  },
  statusPublished: {
    backgroundColor: "#e8f0fe",
    color: "#1769d6",
    fontWeight: 700,
  },
  statusDraft: {
    backgroundColor: "#f1f5f9",
    color: "#64748b",
    fontWeight: 700,
  },
  imageThumb: {
    width: 64,
    height: 64,
    objectFit: "cover",
    borderRadius: 8,
    border: "1px solid #e2e8f0",
  },
  storeThumb: {
    width: 72,
    height: 72,
    objectFit: "cover",
    borderRadius: 8,
    border: "2px solid transparent",
    cursor: "pointer",
  },
  storeThumbSelected: {
    borderColor: "#1769d6",
  },
  infoBtn: {
    padding: 2,
    marginLeft: 2,
    flexShrink: 0,
  },
}));

export default function StudentActivityMatrix() {
  const classes = useStyles();
  const history = useHistory();
  const { userDetails, schooleId } = useMemo(() => getUserContext(), []);

  const [classList, setClassList] = useState([]);
  const [classId, setClassId] = useState("");
  const [sectionList, setSectionList] = useState([]);
  const [sectionId, setSectionId] = useState("");
  const [academicYearId, setAcademicYearId] = useState("");
  const [academicYearLabel, setAcademicYearLabel] = useState(getDefaultAcademicYearLabel());

  const [areaList, setAreaList] = useState([]);
  const [superActivityId, setSuperActivityId] = useState("");
  const [activities, setActivities] = useState([]);
  const [students, setStudents] = useState([]);
  const [recordsMap, setRecordsMap] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});

  const [loadingMeta, setLoadingMeta] = useState(true);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [busyCell, setBusyCell] = useState("");
  const [selectedCell, setSelectedCell] = useState("");
  const [showFullNames, setShowFullNames] = useState(false);
  const [selectedAgeFilter, setSelectedAgeFilter] = useState("");
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [bulkKey, setBulkKey] = useState("P");
  const [bulkApplying, setBulkApplying] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailContext, setDetailContext] = useState(null);
  const [observationText, setObservationText] = useState("");
  const [isListeningObservation, setIsListeningObservation] = useState(false);
  const [savingDetail, setSavingDetail] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState("");
  const [imagesStore, setImagesStore] = useState({
    open: false,
    loading: false,
    rows: [],
    selectedUrls: [],
  });
  const [snack, setSnack] = useState({ open: false, message: "" });

  const fetchIdRef = useRef(0);
  const observationTextRef = useRef("");
  const recognitionRef = useRef(null);

  const showSnack = (message) => setSnack({ open: true, message });

  const filteredAreas = useMemo(() => {
    if (!classId) return [];
    return (areaList || []).filter((a) => !a.classId || normalizeId(a.classId) === classId);
  }, [areaList, classId]);

  const activityGroups = useMemo(() => {
    const list = (activities || []).filter((a) => {
      if (!superActivityId) return true;
      return normalizeId(a.superActivityId) === normalizeId(superActivityId);
    });

    return list.map((activity) => {
      const subs = Array.isArray(activity.subActivities) ? activity.subActivities : [];
      const exerciseEntries = Array.isArray(activity.exercises) ? activity.exercises : [];

      let rows;
      if (subs.length > 0) {
        rows = subs.map((ex) => ({
          activity,
          subActivity: ex,
          id: exerciseRowKey(activity._id, ex._id),
          label: ex.subActivityName || ex.name || "Exercise",
          notes: getRowNotes(activity, ex),
        }));
      } else if (exerciseEntries.some((e) => e?.subActivityId || e?.subActivity?._id)) {
        rows = exerciseEntries
          .filter((e) => e?.subActivityId || e?.subActivity?._id)
          .map((e) => {
            const sub = e.subActivity || { _id: e.subActivityId, subActivityName: e.exerciseName };
            return {
              activity,
              subActivity: sub,
              id: exerciseRowKey(activity._id, sub._id),
              label: e.exerciseName || sub.subActivityName || "Exercise",
              notes: getRowNotes(activity, sub),
            };
          });
      } else {
        rows = [
          {
            activity,
            subActivity: null,
            id: exerciseRowKey(activity._id, null),
            label: activity.activityName || "Activity",
            notes: getRowNotes(activity, null),
          },
        ];
      }

      return {
        id: normalizeId(activity._id),
        title: activity.activityName || "Activity",
        rows,
      };
    });
  }, [activities, superActivityId]);

  const loadActiveYear = useCallback(async () => {
    if (!schooleId) return;
    try {
      const res = await AcademicYearService.getAllAcademicYear(schooleId);
      const list = Array.isArray(res) ? res : [];
      const active =
        list.find((y) => y.isActive || y.status === true || y.status === "active") || list[0];
      if (active) {
        setAcademicYearId(String(active._id || ""));
        setAcademicYearLabel(String(active.acadamicYear || getDefaultAcademicYearLabel()));
      }
    } catch {
      setAcademicYearId("");
      setAcademicYearLabel(getDefaultAcademicYearLabel());
    }
  }, [schooleId]);

  const loadClasses = useCallback(async () => {
    if (!schooleId) return;
    try {
      const res = await AddClassService.getAllAddClass(schooleId);
      const list = Array.isArray(res) ? res : [];
      setClassList(list);
      const preferred =
        normalizeId(userDetails.classId) ||
        (list[0]?._id ? String(list[0]._id) : "");
      if (preferred) setClassId(preferred);
    } catch {
      setClassList([]);
    }
  }, [schooleId, userDetails.classId]);

  const loadSections = useCallback(
    async (nextClassId) => {
      if (!schooleId || !nextClassId) {
        setSectionList([]);
        setSectionId("");
        return;
      }
      try {
        const res = await SectionService.findSectionList({
          schooleId,
          classId: nextClassId,
        });
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : [];
        const filtered = list.filter(
          (s) => !s.classId || normalizeId(s.classId) === normalizeId(nextClassId),
        );
        setSectionList(filtered);
        const preferred = normalizeId(userDetails.sectionId);
        if (preferred && filtered.some((s) => normalizeId(s._id) === preferred)) {
          setSectionId(preferred);
        } else {
          setSectionId("");
        }
      } catch {
        setSectionList([]);
        setSectionId("");
      }
    },
    [schooleId, userDetails.sectionId],
  );

  const loadStudents = useCallback(
    async (nextClassId, nextSectionId) => {
      if (!schooleId || !nextClassId) {
        setStudents([]);
        return;
      }
      try {
        const body = { classId: nextClassId };
        if (nextSectionId) body.sectionId = nextSectionId;
        const res = await StudentService.getAllStudentById(schooleId, body);
        const list = (Array.isArray(res) ? res : []).map((s) => ({
          ...s,
          _id: normalizeId(s._id),
          name: studentDisplayName(s),
          dob: s.dob,
          ageLabel: formatStudentAge(s.dob),
          ageGroupLabel: getAgeGroupLabel(s.dob),
        }));
        setStudents(list);
        setSelectedAgeFilter("");
        setSelectedRowIds([]);
      } catch {
        setStudents([]);
      }
    },
    [schooleId],
  );

  const loadAreasAndActivities = useCallback(
    async (nextClassId) => {
      if (!schooleId || !nextClassId) {
        setAreaList([]);
        setActivities([]);
        setSuperActivityId("");
        setSelectedRowIds([]);
        return;
      }
      try {
        // catalogType is resolved server-side from the school registration.
        const [areas, acts] = await Promise.all([
          SuperActivityService.getAllSuperActivityById(schooleId, {
            classId: nextClassId,
          }),
          ActivityTabelService.getActivitiesWithExercises({
            schooleId,
            classId: nextClassId,
          }),
        ]);
        const areaArr = Array.isArray(areas) ? areas : [];
        const actArr = Array.isArray(acts) ? acts : [];
        setAreaList(areaArr);
        setActivities(actArr);
        setSelectedRowIds([]);

        const classAreas = areaArr.filter(
          (a) => !a.classId || normalizeId(a.classId) === normalizeId(nextClassId),
        );
        const nextArea =
          classAreas[0]?._id ||
          (actArr[0] ? normalizeId(actArr[0].superActivityId) : "");
        setSuperActivityId(nextArea ? String(nextArea) : "");

        const expandMap = {};
        actArr.forEach((a) => {
          if (a?._id) expandMap[normalizeId(a._id)] = true;
        });
        setExpandedGroups(expandMap);
      } catch {
        setAreaList([]);
        setActivities([]);
        setSuperActivityId("");
        setSelectedRowIds([]);
      }
    },
    [schooleId],
  );

  const loadRecords = useCallback(async () => {
    if (!schooleId || !classId || !superActivityId) {
      setRecordsMap({});
      return;
    }
    const requestId = ++fetchIdRef.current;
    setLoadingGrid(true);
    try {
      const res = await ActivityService.getStudentActivityRecords({
        schooleId,
        classId,
        sectionId: sectionId || undefined,
        superActivityId,
        showMaster: true,
      });
      if (requestId !== fetchIdRef.current) return;
      const map = {};
      (Array.isArray(res) ? res : []).forEach((record) => {
        const key = recordCellKey(record.studentId, record.activityId, record.subActivityId);
        if (!map[key]) map[key] = record;
      });
      setRecordsMap(map);
    } catch {
      if (requestId === fetchIdRef.current) setRecordsMap({});
    } finally {
      if (requestId === fetchIdRef.current) setLoadingGrid(false);
    }
  }, [schooleId, classId, sectionId, superActivityId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingMeta(true);
      await Promise.all([loadClasses(), loadActiveYear()]);
      if (!cancelled) setLoadingMeta(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadClasses, loadActiveYear]);

  useEffect(() => {
    if (!classId) return;
    loadSections(classId);
    loadAreasAndActivities(classId);
  }, [classId, loadSections, loadAreasAndActivities]);

  useEffect(() => {
    if (!classId) return;
    loadStudents(classId, sectionId);
  }, [classId, sectionId, loadStudents]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const expandAll = () => {
    const next = {};
    activityGroups.forEach((g) => {
      next[g.id] = true;
    });
    setExpandedGroups(next);
  };

  const collapseAll = () => {
    const next = {};
    activityGroups.forEach((g) => {
      next[g.id] = false;
    });
    setExpandedGroups(next);
  };

  const visibleStudents = useMemo(() => {
    if (!selectedAgeFilter) return students;
    return students.filter((s) => s.ageGroupLabel === selectedAgeFilter);
  }, [students, selectedAgeFilter]);

  const studentAgeWiseSummary = useMemo(() => buildAgeWiseSummary(students), [students]);

  const upsertRecord = (cellKey, record) => {
    setRecordsMap((prev) => ({ ...prev, [cellKey]: record }));
  };

  const stopObservationVoice = useCallback(() => {
    try {
      recognitionRef.current?.stop?.();
    } catch {
      /* ignore */
    }
    recognitionRef.current = null;
    setIsListeningObservation(false);
  }, []);

  useEffect(() => {
    observationTextRef.current = observationText;
  }, [observationText]);

  useEffect(() => () => stopObservationVoice(), [stopObservationVoice]);

  const startObservationVoice = useCallback(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      showSnack("Voice input needs Chrome or Edge");
      return;
    }
    stopObservationVoice();
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        transcript += event.results[i][0].transcript;
      }
      const base = observationTextRef.current || "";
      const next = `${base}${base && !base.endsWith(" ") ? " " : ""}${transcript}`.trimStart();
      observationTextRef.current = next;
      setObservationText(next);
    };
    recognition.onerror = () => {
      stopObservationVoice();
    };
    recognition.onend = () => {
      setIsListeningObservation(false);
    };
    recognitionRef.current = recognition;
    try {
      recognition.start();
      setIsListeningObservation(true);
    } catch {
      stopObservationVoice();
    }
  }, [stopObservationVoice]);

  const toggleObservationVoice = () => {
    if (isListeningObservation) stopObservationVoice();
    else startObservationVoice();
  };

  const openDetail = (student, row) => {
    const cellKey = recordCellKey(student._id, row.activity._id, row.subActivity?._id);
    const record = recordsMap[cellKey] || null;
    setSelectedCell(cellKey);
    setDetailContext({ student, row, record, cellKey });
    const remarks = record?.remarks || "";
    observationTextRef.current = remarks;
    setObservationText(remarks);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    stopObservationVoice();
    setDetailOpen(false);
    setDetailContext(null);
    observationTextRef.current = "";
    setObservationText("");
    setImagesStore({ open: false, loading: false, rows: [], selectedUrls: [] });
  };

  const ensureRecordForDetail = async () => {
    if (detailContext?.record?._id) return detailContext.record;
    const { student, row, cellKey } = detailContext;
    const today = getTodayIso();
    const response = await ActivityService.addQuickActivityRecord({
      schooleId,
      classId,
      sectionId: normalizeId(sectionId) || normalizeId(student.sectionId) || null,
      studentId: student._id,
      activityId: normalizeId(row.activity._id),
      superActivityId: normalizeId(row.activity.superActivityId) || superActivityId,
      subActivityId: normalizeId(row.subActivity?._id) || null,
      key: "P",
      date: today,
      endDate: today,
      sfd: today,
      remarks: "",
      isFuturePlanning: false,
      authorizedPerson: userDetails.email || "",
      academicYear: academicYearLabel || undefined,
      academicYearId: academicYearId || undefined,
      teacherId: normalizeId(userDetails.teacherId) || null,
    });
    const record = response?.activityRecord;
    if (!record) throw new Error("Could not create activity record");
    upsertRecord(cellKey, record);
    setDetailContext((prev) => ({ ...prev, record }));
    return record;
  };

  const saveObservation = async () => {
    if (!detailContext) return;
    setSavingDetail(true);
    try {
      let record = detailContext.record;
      if (!record?._id) record = await ensureRecordForDetail();
      await ActivityService.patchActivityRecord({
        _id: record._id,
        remarks: observationText.trim(),
      });
      const updated = { ...record, remarks: observationText.trim() };
      upsertRecord(detailContext.cellKey, updated);
      setDetailContext((prev) => ({ ...prev, record: updated }));
      showSnack("Observation saved");
      closeDetail();
    } catch (err) {
      showSnack(err?.response?.data?.message || "Failed to save observation");
    } finally {
      setSavingDetail(false);
    }
  };

  const handlePublicStatusChange = async (record, publicStatus) => {
    if (!record?._id) return;
    setUpdatingStatus(record._id);
    try {
      await ActivityService.upadePublicStatus({ _id: record._id, publicStatus });
      const updated = { ...record, publicStatus };
      if (detailContext?.cellKey) upsertRecord(detailContext.cellKey, updated);
      setDetailContext((prev) => (prev ? { ...prev, record: updated } : prev));
      showSnack(
        publicStatus === "publish"
          ? "Activity published to parents"
          : "Activity moved back to draft",
      );
    } catch (err) {
      showSnack(err?.response?.data?.message || "Failed to update publish status");
    } finally {
      setUpdatingStatus("");
    }
  };

  const handleImageUpload = async (event) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (!files.length || !detailContext) return;
    setUploadingImage(true);
    try {
      let record = detailContext.record;
      if (!record?._id) record = await ensureRecordForDetail();
      const existing = Array.isArray(record.imageURL) ? record.imageURL.filter(Boolean) : [];
      const remaining = MAX_IMAGES - existing.length;
      const toUpload = files.slice(0, remaining);
      const uploadResults = await Promise.all(
        toUpload.map((file) => {
          const formData = new FormData();
          formData.append("image", file);
          formData.append("imageName", file.name);
          return StudentService.uploadImage(formData);
        }),
      );
      const urls = uploadResults.map((r) => r?.url).filter(Boolean);
      if (!urls.length) throw new Error("Upload failed");
      const imageURL = [...existing, ...urls];
      await ActivityService.patchActivityRecord({ _id: record._id, imageURL });
      const updated = { ...record, imageURL };
      upsertRecord(detailContext.cellKey, updated);
      setDetailContext((prev) => ({ ...prev, record: updated }));
      showSnack("Image uploaded");
    } catch (err) {
      showSnack(err?.response?.data?.message || err.message || "Upload failed");
    } finally {
      setUploadingImage(false);
      if (event.target) event.target.value = "";
    }
  };

  const openImagesStore = async () => {
    if (!schooleId || !classId) return;
    setImagesStore((p) => ({ ...p, open: true, loading: true, rows: [], selectedUrls: [] }));
    try {
      const body = { schooleId, classId };
      if (sectionId) body.sectionId = sectionId;
      const rows = await ImagesStoreService.findImagesStoreList(body);
      setImagesStore((p) => ({
        ...p,
        loading: false,
        rows: Array.isArray(rows) ? rows : [],
      }));
    } catch {
      setImagesStore((p) => ({ ...p, loading: false, rows: [] }));
      showSnack("Failed to load Images Store");
    }
  };

  const applyImagesFromStore = async () => {
    if (!detailContext || !imagesStore.selectedUrls.length) return;
    setUploadingImage(true);
    try {
      let record = detailContext.record;
      if (!record?._id) record = await ensureRecordForDetail();
      const existing = Array.isArray(record.imageURL) ? record.imageURL.filter(Boolean) : [];
      const remaining = MAX_IMAGES - existing.length;
      const newUrls = imagesStore.selectedUrls
        .filter((url) => !existing.includes(url))
        .slice(0, remaining);
      const imageURL = [...existing, ...newUrls];
      await ActivityService.patchActivityRecord({ _id: record._id, imageURL });
      const updated = { ...record, imageURL };
      upsertRecord(detailContext.cellKey, updated);
      setDetailContext((prev) => ({ ...prev, record: updated }));
      setImagesStore((p) => ({ ...p, open: false, selectedUrls: [] }));
      showSnack("Images added from store");
    } catch (err) {
      showSnack(err?.response?.data?.message || "Failed to add images");
    } finally {
      setUploadingImage(false);
    }
  };

  const applyStatusToCell = async (student, row, targetKey, mapSnapshot) => {
    const studentId = normalizeId(student._id);
    const activityId = normalizeId(row.activity._id);
    const subActivityId = normalizeId(row.subActivity?._id);
    const cellKey = recordCellKey(studentId, activityId, subActivityId);
    const existing = mapSnapshot[cellKey];
    const currentKey = String(existing?.key || "").trim().toUpperCase();
    const nextKey = String(targetKey || "").trim().toUpperCase();

    if (currentKey === nextKey) {
      return { cellKey, record: existing || null, skipped: true };
    }

    if (existing?._id) {
      const response = await ActivityService.updateActivityStatus({
        id: existing._id,
        key: nextKey,
      });
      const updated = {
        ...existing,
        ...(response?.activityRecord || {}),
        key: nextKey,
      };
      return { cellKey, record: updated, skipped: false };
    }

    if (!nextKey) {
      return { cellKey, record: existing || null, skipped: true };
    }

    const today = getTodayIso();
    const response = await ActivityService.addQuickActivityRecord({
      schooleId,
      classId,
      sectionId: normalizeId(sectionId) || normalizeId(student.sectionId) || null,
      studentId,
      activityId,
      superActivityId: normalizeId(row.activity.superActivityId) || superActivityId,
      subActivityId: subActivityId || null,
      key: nextKey,
      date: today,
      endDate: today,
      sfd: today,
      remarks: "",
      isFuturePlanning: false,
      authorizedPerson: userDetails.email || "",
      academicYear: academicYearLabel || undefined,
      academicYearId: academicYearId || undefined,
      teacherId: normalizeId(userDetails.teacherId) || null,
    });
    return { cellKey, record: response?.activityRecord || null, skipped: false };
  };

  const handleCellClick = async (student, row, { undo = false } = {}) => {
    const studentId = normalizeId(student._id);
    const activityId = normalizeId(row.activity._id);
    const subActivityId = normalizeId(row.subActivity?._id);
    const cellKey = recordCellKey(studentId, activityId, subActivityId);
    if (busyCell === cellKey || bulkApplying) return;

    setSelectedCell(cellKey);
    const existing = recordsMap[cellKey];
    const currentKey = String(existing?.key || "").trim().toUpperCase();
    const targetKey = undo ? prevStatusKey(currentKey) : nextStatusKey(currentKey);

    // Nothing to undo on an empty cell.
    if (undo && !currentKey) return;
    // Already at target (e.g. clearing empty) — skip.
    if (currentKey === targetKey) return;

    setBusyCell(cellKey);
    try {
      const result = await applyStatusToCell(student, row, targetKey, recordsMap);
      if (result.record) {
        setRecordsMap((prev) => ({ ...prev, [result.cellKey]: result.record }));
      } else if (!targetKey && existing?._id) {
        setRecordsMap((prev) => ({
          ...prev,
          [cellKey]: { ...existing, key: "" },
        }));
      }
    } catch (err) {
      showSnack(err?.response?.data?.message || "Failed to update status");
    } finally {
      setBusyCell("");
    }
  };

  const allExerciseRows = useMemo(
    () => activityGroups.flatMap((group) => group.rows || []),
    [activityGroups],
  );

  const allExerciseRowIdSet = useMemo(
    () => new Set(allExerciseRows.map((row) => row.id)),
    [allExerciseRows],
  );

  const selectedRowCount = useMemo(
    () => selectedRowIds.filter((id) => allExerciseRowIdSet.has(id)).length,
    [selectedRowIds, allExerciseRowIdSet],
  );

  const allRowsSelected =
    allExerciseRows.length > 0 && selectedRowCount === allExerciseRows.length;

  const toggleRowSelected = (rowId) => {
    if (!rowId) return;
    setSelectedRowIds((prev) =>
      prev.includes(rowId) ? prev.filter((x) => x !== rowId) : [...prev, rowId],
    );
  };

  const toggleSelectAllRows = () => {
    if (allRowsSelected) {
      setSelectedRowIds((prev) => prev.filter((id) => !allExerciseRowIdSet.has(id)));
      return;
    }
    setSelectedRowIds(allExerciseRows.map((row) => row.id));
  };

  const applyBulkKeyToStudent = async (student) => {
    const targets = allExerciseRows.filter((row) => selectedRowIds.includes(row.id));
    if (!targets.length || !student || bulkApplying) return;

    setBulkApplying(true);
    try {
      let mapSnapshot = { ...recordsMap };
      let updatedCount = 0;
      const patch = {};

      for (const row of targets) {
        const result = await applyStatusToCell(student, row, bulkKey, mapSnapshot);
        if (result.record) {
          mapSnapshot = { ...mapSnapshot, [result.cellKey]: result.record };
          patch[result.cellKey] = result.record;
          if (!result.skipped) updatedCount += 1;
        } else if (!bulkKey) {
          const existing = mapSnapshot[result.cellKey];
          if (existing?._id) {
            const cleared = { ...existing, key: "" };
            mapSnapshot = { ...mapSnapshot, [result.cellKey]: cleared };
            patch[result.cellKey] = cleared;
            updatedCount += 1;
          }
        }
      }

      if (Object.keys(patch).length) {
        setRecordsMap((prev) => ({ ...prev, ...patch }));
      }

      const label = bulkKey ? STATUS_META[bulkKey]?.short || bulkKey : "Empty";
      showSnack(
        updatedCount
          ? `Set ${updatedCount} activit${updatedCount === 1 ? "y" : "ies"} to ${label} for ${student.name}`
          : `Selected activities already set to ${label} for ${student.name}`,
      );
    } catch (err) {
      showSnack(err?.response?.data?.message || "Failed to update selected activities");
    } finally {
      setBulkApplying(false);
    }
  };

  /** Apply selected key to every visible student for one activity/exercise row. */
  const applyBulkKeyToActivityRow = async (row) => {
    if (!row || !visibleStudents.length || bulkApplying) return;

    setBulkApplying(true);
    try {
      let mapSnapshot = { ...recordsMap };
      let updatedCount = 0;
      const patch = {};

      for (const student of visibleStudents) {
        const result = await applyStatusToCell(student, row, bulkKey, mapSnapshot);
        if (result.record) {
          mapSnapshot = { ...mapSnapshot, [result.cellKey]: result.record };
          patch[result.cellKey] = result.record;
          if (!result.skipped) updatedCount += 1;
        } else if (!bulkKey) {
          const existing = mapSnapshot[result.cellKey];
          if (existing?._id) {
            const cleared = { ...existing, key: "" };
            mapSnapshot = { ...mapSnapshot, [result.cellKey]: cleared };
            patch[result.cellKey] = cleared;
            updatedCount += 1;
          }
        }
      }

      if (Object.keys(patch).length) {
        setRecordsMap((prev) => ({ ...prev, ...patch }));
      }

      const label = bulkKey ? STATUS_META[bulkKey]?.short || bulkKey : "Empty";
      showSnack(
        updatedCount
          ? `Set ${label} for ${updatedCount} student${updatedCount === 1 ? "" : "s"} on ${row.label}`
          : `All students already set to ${label} on ${row.label}`,
      );
    } catch (err) {
      showSnack(err?.response?.data?.message || "Failed to update activity for all students");
    } finally {
      setBulkApplying(false);
    }
  };

  /** Apply selected key to all selected activities × all visible students. */
  const applyBulkKeyToAllStudents = async () => {
    const targets = allExerciseRows.filter((row) => selectedRowIds.includes(row.id));
    if (!targets.length || !visibleStudents.length || bulkApplying) return;

    setBulkApplying(true);
    try {
      let mapSnapshot = { ...recordsMap };
      let updatedCount = 0;
      const patch = {};

      for (const row of targets) {
        for (const student of visibleStudents) {
          const result = await applyStatusToCell(student, row, bulkKey, mapSnapshot);
          if (result.record) {
            mapSnapshot = { ...mapSnapshot, [result.cellKey]: result.record };
            patch[result.cellKey] = result.record;
            if (!result.skipped) updatedCount += 1;
          } else if (!bulkKey) {
            const existing = mapSnapshot[result.cellKey];
            if (existing?._id) {
              const cleared = { ...existing, key: "" };
              mapSnapshot = { ...mapSnapshot, [result.cellKey]: cleared };
              patch[result.cellKey] = cleared;
              updatedCount += 1;
            }
          }
        }
      }

      if (Object.keys(patch).length) {
        setRecordsMap((prev) => ({ ...prev, ...patch }));
      }

      const label = bulkKey ? STATUS_META[bulkKey]?.short || bulkKey : "Empty";
      showSnack(
        updatedCount
          ? `Set ${updatedCount} cell${updatedCount === 1 ? "" : "s"} to ${label} for all students`
          : `Selected activities already set to ${label} for all students`,
      );
    } catch (err) {
      showSnack(err?.response?.data?.message || "Failed to update selected activities");
    } finally {
      setBulkApplying(false);
    }
  };

  const selectedAreaName =
    filteredAreas.find((a) => normalizeId(a._id) === normalizeId(superActivityId))
      ?.superActivityName || "Area of Work";

  const detailImages =
    detailContext?.record?.imageURL && Array.isArray(detailContext.record.imageURL)
      ? detailContext.record.imageURL.filter(Boolean)
      : [];

  const storeVisibleImages = imagesStore.rows.flatMap((row) =>
    (row.imageURL || [])
      .map((url) => String(url || "").trim())
      .filter(Boolean),
  );

  return (
    <Box className={classes.root}>
      <Box className={classes.topBar}>
        <IconButton
          className={classes.backBtn}
          size="small"
          onClick={() => history.push("/app/studentactivity")}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box className={classes.classTabs}>
          {(classList || []).map((c) => {
            const cid = String(c._id);
            const active = cid === String(classId || "");
            return (
              <Box
                key={c._id}
                role="button"
                tabIndex={0}
                className={`${classes.classTab} ${active ? classes.classTabActive : ""}`}
                onClick={() => setClassId(cid)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setClassId(cid);
                }}
              >
                {c.className || "Class"}
              </Box>
            );
          })}
        </Box>
        {sectionList.length > 0 ? (
          <FormControl variant="outlined" size="small">
            <Select
              className={classes.sectionSelect}
              value={sectionId}
              onChange={(e) => setSectionId(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">All sections</MenuItem>
              {sectionList.map((s) => (
                <MenuItem key={s._id} value={String(s._id)}>
                  {s.sectionName || "Section"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
      </Box>

      {students.length > 0 ? (
        <Box className={classes.ageBar}>
          <Typography className={classes.ageLabel}>Age Group</Typography>
          <Chip
            size="small"
            label={`All (${students.length})`}
            clickable
            onClick={() => {
              setSelectedAgeFilter("");
            }}
            style={{
              backgroundColor: !selectedAgeFilter ? "#1769d6" : "#e8f0fe",
              color: !selectedAgeFilter ? "#fff" : "#1769d6",
              fontWeight: 700,
            }}
          />
          {studentAgeWiseSummary.map(([label, count]) => {
            const isActive = selectedAgeFilter === label;
            return (
              <Chip
                key={label}
                size="small"
                clickable
                label={`${label} (${count})`}
                onClick={() => {
                  setSelectedAgeFilter((prev) => (prev === label ? "" : label));
                }}
                variant={isActive ? "default" : "outlined"}
                style={
                  isActive
                    ? { backgroundColor: "#1769d6", color: "#fff", fontWeight: 600 }
                    : undefined
                }
              />
            );
          })}
        </Box>
      ) : null}

      <Box className={classes.areaBar}>
        <Box className={classes.areaSelectWrap}>
          <Select
            className={classes.areaSelect}
            value={superActivityId}
            onChange={(e) => {
              setSuperActivityId(e.target.value);
              setSelectedRowIds([]);
            }}
            disableUnderline
            IconComponent={ExpandMoreIcon}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select area of work
            </MenuItem>
            {filteredAreas.map((a) => (
              <MenuItem key={a._id} value={String(a._id)}>
                {a.superActivityName || "Area"}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Tooltip title="Expand all">
          <IconButton className={classes.toolBtn} size="small" onClick={expandAll}>
            <UnfoldMoreIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Collapse all">
          <IconButton className={classes.toolBtn} size="small" onClick={collapseAll}>
            <UnfoldLessIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button
          size="small"
          variant="outlined"
          className={classes.nameToggle}
          onClick={() => setShowFullNames((v) => !v)}
        >
          {showFullNames ? "Short names" : "Full names"}
        </Button>
      </Box>

      {selectedRowCount > 0 ? (
        <Box className={classes.bulkBar}>
          <Typography className={classes.bulkHint}>
            {selectedRowCount} activit{selectedRowCount === 1 ? "y" : "ies"} selected — choose key,
            then Apply on a student or Apply on the activity for all students
          </Typography>
          {KEY_SEQUENCE.map((key) => {
            const meta = STATUS_META[key];
            const active = bulkKey === key;
            return (
              <Button
                key={key}
                size="small"
                variant={active ? "contained" : "outlined"}
                className={classes.bulkKeyBtn}
                style={
                  active
                    ? { background: meta.bg, color: meta.color, borderColor: meta.bg }
                    : { borderColor: meta.bg, color: meta.bg }
                }
                onClick={() => setBulkKey(key)}
                disabled={bulkApplying}
              >
                {meta.short}
              </Button>
            );
          })}
          <Button
            size="small"
            variant={bulkKey === "" ? "contained" : "outlined"}
            className={classes.bulkKeyBtn}
            style={
              bulkKey === ""
                ? { background: "#64748b", color: "#fff" }
                : { borderColor: "#94a3b8", color: "#64748b" }
            }
            onClick={() => setBulkKey("")}
            disabled={bulkApplying}
          >
            Empty
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={bulkApplying || visibleStudents.length === 0}
            onClick={applyBulkKeyToAllStudents}
            style={{ textTransform: "none", fontWeight: 700 }}
          >
            Apply {bulkKey || "∅"} to all students
          </Button>
          <Button
            size="small"
            onClick={() => setSelectedRowIds([])}
            disabled={bulkApplying}
            style={{ textTransform: "none", fontWeight: 700, color: "#1769d6" }}
          >
            Clear selection
          </Button>
          {bulkApplying ? <CircularProgress size={18} /> : null}
        </Box>
      ) : null}

      <Box className={classes.gridWrap}>
        {loadingMeta || loadingGrid ? (
          <Box className={classes.loadingOverlay}>
            <CircularProgress size={36} />
          </Box>
        ) : !classId ? (
          <Typography className={classes.emptyState}>Select a class to begin.</Typography>
        ) : students.length === 0 ? (
          <Typography className={classes.emptyState}>
            No students found for this class{sectionId ? " / section" : ""}.
          </Typography>
        ) : visibleStudents.length === 0 ? (
          <Typography className={classes.emptyState}>
            No students in the selected age group.
          </Typography>
        ) : activityGroups.length === 0 ? (
          <Typography className={classes.emptyState}>
            No activities found for this area of work.
          </Typography>
        ) : (
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.stickyCorner}>
                  <Typography variant="caption" style={{ color: "#94a3b8", fontWeight: 700 }}>
                    Activities / Exercises
                  </Typography>
                  <Typography
                    variant="caption"
                    style={{ display: "block", color: "#94a3b8", marginTop: 2 }}
                  >
                    {selectedAreaName}
                  </Typography>
                  {allExerciseRows.length > 0 ? (
                    <FormControlLabel
                      style={{ margin: "6px 0 0", display: "flex" }}
                      control={
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={allRowsSelected}
                          indeterminate={selectedRowCount > 0 && !allRowsSelected}
                          onChange={toggleSelectAllRows}
                          disabled={bulkApplying}
                        />
                      }
                      label={
                        <Typography variant="caption" style={{ fontWeight: 700, color: "#64748b" }}>
                          Select all activities
                        </Typography>
                      }
                    />
                  ) : null}
                </th>
                {visibleStudents.map((s) => (
                  <th
                    key={s._id}
                    className={`${classes.studentHead} ${
                      showFullNames ? classes.studentHeadFull : ""
                    } ${selectedRowCount > 0 ? classes.studentHeadSelected : ""}`}
                  >
                    <Tooltip
                      title={
                        s.ageLabel ? `${s.name} (${s.ageLabel})` : s.name || ""
                      }
                      placement="top"
                    >
                      <div
                        className={`${classes.studentName} ${
                          showFullNames ? classes.studentNameFull : ""
                        }`}
                      >
                        {s.name}
                      </div>
                    </Tooltip>
                    {selectedRowCount > 0 ? (
                      <Button
                        size="small"
                        variant="outlined"
                        className={classes.applyColBtn}
                        disabled={bulkApplying}
                        onClick={() => applyBulkKeyToStudent(s)}
                      >
                        Apply {bulkKey || "∅"}
                      </Button>
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {activityGroups.map((group) => {
                const open = expandedGroups[group.id] !== false;
                return (
                  <React.Fragment key={group.id}>
                    <tr className={classes.groupRow}>
                      <td className={classes.groupCell} onClick={() => toggleGroup(group.id)}>
                        <div className={classes.groupTitle} title={group.title}>
                          {open ? (
                            <KeyboardArrowDownIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowRightIcon fontSize="small" />
                          )}
                          {group.title}
                        </div>
                      </td>
                      {visibleStudents.map((s) => (
                        <td key={`${group.id}-${s._id}`} className={classes.groupFill} />
                      ))}
                    </tr>
                    {open
                      ? group.rows.map((row) => {
                          const rowSelected = selectedRowIds.includes(row.id);
                          return (
                          <tr
                            key={row.id}
                            className={rowSelected ? classes.activityRowSelected : undefined}
                          >
                            <td
                              className={`${classes.activityCell} ${
                                rowSelected ? classes.activityRowSelected : ""
                              }`}
                            >
                              <div className={classes.activityLabel}>
                                <Checkbox
                                  className={classes.activityRowCheck}
                                  size="small"
                                  color="primary"
                                  checked={rowSelected}
                                  disabled={bulkApplying}
                                  onChange={() => toggleRowSelected(row.id)}
                                  inputProps={{ "aria-label": `Select ${row.label}` }}
                                />
                                <span className={classes.activityIcon}>
                                  <PlayArrowIcon />
                                </span>
                                <span className={classes.activityName} title={row.label}>
                                  {row.label}
                                </span>
                                {row.notes ? (
                                  <Tooltip title={`${row.notes} — click to open gallery`}>
                                    <IconButton
                                      className={classes.infoBtn}
                                      size="small"
                                      aria-label="Activity explanation"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const path = resolveMediaGalleryPath(
                                          row.activity,
                                          row.subActivity,
                                        );
                                        if (path) history.push(path);
                                      }}
                                    >
                                      <InfoOutlinedIcon
                                        style={{ fontSize: 16, color: "#1769d6" }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                ) : null}
                                {rowSelected ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    className={classes.applyRowBtn}
                                    disabled={bulkApplying || visibleStudents.length === 0}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      applyBulkKeyToActivityRow(row);
                                    }}
                                  >
                                    Apply {bulkKey || "∅"} to all students
                                  </Button>
                                ) : null}
                              </div>
                            </td>
                            {visibleStudents.map((student) => {
                              const cellKey = recordCellKey(
                                student._id,
                                row.activity._id,
                                row.subActivity?._id,
                              );
                              const record = recordsMap[cellKey];
                              const status = String(record?.key || "").trim().toUpperCase();
                              const meta = STATUS_META[status];
                              const busy = busyCell === cellKey;
                              const hasRemarks = Boolean(String(record?.remarks || "").trim());
                              const hasImages =
                                Array.isArray(record?.imageURL) &&
                                record.imageURL.filter(Boolean).length > 0;
                              const hasStatus = Boolean(meta);
                              const showAddIcon = hasStatus;
                              const addIconFilled = hasRemarks || hasImages;
                              return (
                                <td
                                  key={cellKey}
                                  className={`${classes.statusTd} ${
                                    rowSelected ? classes.statusTdSelected : ""
                                  }`}
                                >
                                  <Box className={classes.cellActions}>
                                    <Tooltip
                                      title={
                                        meta
                                          ? `${meta.label} — click next · right-click or Shift+click undo`
                                          : "Empty — click for Presented"
                                      }
                                    >
                                      <button
                                        type="button"
                                        className={`${classes.statusBtn} ${
                                          selectedCell === cellKey ? classes.statusBtnActive : ""
                                        }`}
                                        style={
                                          meta
                                            ? {
                                                background: meta.bg,
                                                color: meta.color,
                                                borderColor: meta.bg,
                                              }
                                            : undefined
                                        }
                                        disabled={busy || bulkApplying}
                                        onClick={(e) => {
                                          handleCellClick(student, row, {
                                            undo: Boolean(e.shiftKey),
                                          });
                                        }}
                                        onContextMenu={(e) => {
                                          e.preventDefault();
                                          handleCellClick(student, row, { undo: true });
                                        }}
                                      >
                                        {busy ? "…" : meta ? meta.short : ""}
                                      </button>
                                    </Tooltip>
                                    {showAddIcon ? (
                                      <Tooltip
                                        title={
                                          addIconFilled
                                            ? "View / edit observation, images & publish"
                                            : "Add observation, images & publish"
                                        }
                                      >
                                        <IconButton
                                          className={`${classes.addCellBtn} ${
                                            addIconFilled ? classes.addCellBtnFilled : ""
                                          }`}
                                          size="small"
                                          aria-label="Add details"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openDetail(student, row);
                                          }}
                                        >
                                          <AddIcon />
                                        </IconButton>
                                      </Tooltip>
                                    ) : null}
                                  </Box>
                                </td>
                              );
                            })}
                          </tr>
                          );
                        })
                      : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </Box>

      <Box className={classes.legend}>
        <span className={classes.legendItem}>
          <span className={classes.legendSwatch} /> Empty — no status yet
        </span>
        {KEY_SEQUENCE.map((key) => {
          const meta = STATUS_META[key];
          return (
            <span key={key} className={classes.legendItem}>
              <span
                className={classes.legendSwatch}
                style={{ background: meta.bg, color: meta.color, borderColor: meta.bg }}
              >
                {meta.short}
              </span>
              {meta.label}
            </span>
          );
        })}
        <span className={classes.legendItem}>
          Click = next key · Right-click / Shift+click = undo previous key
        </span>
        <span className={classes.legendItem}>
          <span className={`${classes.legendPlus} ${classes.legendPlusBlue}`}>+</span>
          Blue + — status set; tap to add observation / images
        </span>
        <span className={classes.legendItem}>
          <span className={`${classes.legendPlus} ${classes.legendPlusGreen}`}>+</span>
          Green + — observation or images already saved
        </span>
      </Box>

      <Drawer
        anchor="right"
        open={detailOpen}
        onClose={closeDetail}
        classes={{ paper: classes.drawerPaper }}
      >
        {detailContext ? (
          <>
            <Box display="flex" alignItems="flex-start" justifyContent="space-between">
              <Box>
                <Typography style={{ fontWeight: 700, color: "#0f2d4d" }}>
                  {detailContext.row?.activity?.activityName || "Activity"}
                  {detailContext.row?.notes ? (
                    <Tooltip title={`${detailContext.row.notes} — click to open gallery`}>
                      <IconButton
                        size="small"
                        className={classes.infoBtn}
                        onClick={() => {
                          const path = resolveMediaGalleryPath(
                            detailContext.row.activity,
                            detailContext.row.subActivity,
                          );
                          if (path) history.push(path);
                        }}
                      >
                        <InfoOutlinedIcon style={{ fontSize: 18, color: "#1769d6" }} />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Typography>
                {detailContext.row?.subActivity?.subActivityName ? (
                  <Typography style={{ fontSize: "0.85rem", color: "#9aa6b6" }}>
                    {detailContext.row.subActivity.subActivityName}
                  </Typography>
                ) : null}
                <Typography style={{ fontSize: "0.8rem", color: "#1769d6", marginTop: 4 }}>
                  {detailContext.student?.name}
                  {detailContext.student?.ageLabel
                    ? ` (${detailContext.student.ageLabel})`
                    : ""}
                </Typography>
              </Box>
              <IconButton size="small" onClick={closeDetail}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box>
              <Typography className={classes.topLabel}>Publish Status</Typography>
              <Box mt={0.5}>
                {detailContext.record?._id ? (
                  <Chip
                    size="small"
                    label={
                      updatingStatus === detailContext.record._id
                        ? "..."
                        : isRecordPublished(detailContext.record)
                          ? "Published"
                          : "Draft"
                    }
                    className={
                      isRecordPublished(detailContext.record)
                        ? classes.statusPublished
                        : classes.statusDraft
                    }
                    onClick={() =>
                      handlePublicStatusChange(
                        detailContext.record,
                        isRecordPublished(detailContext.record) ? "draft" : "publish",
                      )
                    }
                    disabled={updatingStatus === detailContext.record._id}
                  />
                ) : (
                  <Typography style={{ fontSize: "0.85rem", color: "#9aa6b6" }}>
                    Save a record first to publish
                  </Typography>
                )}
              </Box>
            </Box>

            <TextField
              label="Observation"
              placeholder={
                isListeningObservation
                  ? "Listening… speak now"
                  : "Add observation notes… or tap mic to speak"
              }
              multiline
              rows={5}
              variant="outlined"
              fullWidth
              value={observationText}
              onChange={(e) => setObservationText(e.target.value)}
              helperText={
                isListeningObservation
                  ? "Voice to text is on. Tap the mic again to stop."
                  : "Tap the mic to dictate into this box (Chrome / Edge)."
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    style={{ alignSelf: "flex-start", marginTop: 8 }}
                  >
                    <Tooltip
                      title={
                        isListeningObservation ? "Stop voice input" : "Start voice to text"
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={toggleObservationVoice}
                        style={{
                          color: isListeningObservation ? "#c62828" : "#1769d6",
                          backgroundColor: isListeningObservation ? "#ffebee" : "#e8f0fe",
                        }}
                      >
                        {isListeningObservation ? (
                          <MicOffIcon fontSize="small" />
                        ) : (
                          <MicIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />

            <Box>
              <Typography className={classes.topLabel}>Images</Typography>
              <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginTop: 8 }}>
                {detailImages.map((url) => (
                  <img key={url} src={url} alt="" className={classes.imageThumb} />
                ))}
                {detailImages.length < MAX_IMAGES ? (
                  <>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      startIcon={
                        uploadingImage ? (
                          <CircularProgress size={16} />
                        ) : (
                          <PhotoCameraIcon />
                        )
                      }
                      disabled={uploadingImage}
                    >
                      Upload
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                      />
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PhotoLibraryIcon />}
                      onClick={openImagesStore}
                      disabled={uploadingImage}
                    >
                      From Store
                    </Button>
                  </>
                ) : null}
              </Box>
            </Box>

            <Box mt="auto" display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
              <Button onClick={closeDetail}>Close</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={saveObservation}
                disabled={savingDetail}
                style={{ background: "#1769d6" }}
              >
                {savingDetail ? "Saving..." : "Save Observation"}
              </Button>
            </Box>
          </>
        ) : null}
      </Drawer>

      <Dialog
        open={imagesStore.open}
        onClose={() => setImagesStore((p) => ({ ...p, open: false }))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Images Store</DialogTitle>
        <DialogContent dividers>
          {imagesStore.loading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : storeVisibleImages.length === 0 ? (
            <Typography color="textSecondary">No images found for this class.</Typography>
          ) : (
            <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
              {storeVisibleImages.map((url) => {
                const selected = imagesStore.selectedUrls.includes(url);
                return (
                  <img
                    key={url}
                    src={url}
                    alt=""
                    className={`${classes.storeThumb} ${
                      selected ? classes.storeThumbSelected : ""
                    }`}
                    onClick={() =>
                      setImagesStore((p) => ({
                        ...p,
                        selectedUrls: selected
                          ? p.selectedUrls.filter((u) => u !== url)
                          : [...p.selectedUrls, url],
                      }))
                    }
                  />
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImagesStore((p) => ({ ...p, open: false }))}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={!imagesStore.selectedUrls.length || uploadingImage}
            onClick={applyImagesFromStore}
          >
            Add selected
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ open: false, message: "" })}
        message={snack.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
